import React from 'react'
import styled from '@emotion/styled'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Button,
  Portal,
  Text,
  Flex,
  Image,
} from '@chakra-ui/core'
import setupMail from 'assets/setup_mail.webp'
import setupAI from 'assets/setup_ai.webp'
import setupData from 'assets/setup_data.webp'
import setupKPIs from 'assets/setup_kpis.webp'
import setupOrganization from 'assets/setup_org_tree.webp'
import setupSalesOpportunities from 'assets/setup_sales_opport.webp'
import { STEPS } from 'utils/setupStorage'
import useLocaleText from 'components/useLocaleText'
import LocaleText from 'components/LocaleText'
import { keyframes } from '@emotion/core'

interface SetupStepPopoverProps {
  stepNumber: number
  title: string
  description: string
  isOpen: boolean
  onClose: () => void
  onGetStarted: () => void
  onSkip: () => void
  children: React.ReactNode
  totalSteps?: number
}

const getPopverImg = (stepNumber: number) => {
  switch (stepNumber) {
    case STEPS.FEED:
      return setupMail
    case STEPS.ORGANIZATION:
      return setupOrganization
    case STEPS.AI_FEED:
      return setupAI
    case STEPS.REPORTS:
      return setupData
    case STEPS.RANKINGS_LIST:
      return setupKPIs
    case STEPS.FIND_SALES_OPPORTUNITIES:
      return setupSalesOpportunities
    default:
      return null
  }
}

export const SetupStepPopover: React.FC<SetupStepPopoverProps> = ({
  stepNumber,
  title,
  description,
  isOpen,
  onClose,
  onGetStarted,
  onSkip,
  children,
  totalSteps = Object.keys(STEPS).length,
}) => {
  const t_step = useLocaleText('step', true)
  const t_get_started = useLocaleText('get_started', true)
  const t_skip = useLocaleText('t_skip', true)

  const getPlacement = () => {
    if (stepNumber === STEPS.FEED) {
      return 'top-start'
    }
    if (stepNumber === totalSteps) {
      return 'top-end'
    }
    return 'top'
  }

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement={getPlacement()} closeOnBlur={false} gutter={40}>
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent
          zIndex={1500}
          bg="white"
          top="-20px !important"
          borderRadius="16px"
          boxShadow="0 10px 40px rgba(0, 0, 0, 0.15)"
          maxW="600px"
          w="600px"
          border="none"
          overflow="hidden"
          style={{ direction: 'ltr' }}
          _focus={{ outline: 'none', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)' }}
        >
          <PopoverBody p={0} w="100%" display="flex">
            <Flex flexDir="column" justifyContent="space-between" w="50%" p={4}>
              <Flex flexDir="column">
                <Text mb={4}>
                  {t_step} {stepNumber}
                </Text>
                <PopoverTitle>
                  <LocaleText text={title} isLocalUse={true} />
                </PopoverTitle>
                <PopoverDescription>
                  <LocaleText text={description} isLocalUse={true} />
                </PopoverDescription>
              </Flex>

              <ButtonContainer>
                <SkipButton onClick={onSkip}>{t_skip}</SkipButton>
                <GetStartedButton onClick={onGetStarted}>{t_get_started}</GetStartedButton>
              </ButtonContainer>
            </Flex>

            <PreviewImage>
              <Image src={getPopverImg(stepNumber) || ''} alt={title} width="100%" height="100%" objectFit="cover" />
            </PreviewImage>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

const PreviewImage = styled.div`
  width: 300px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

const PopoverTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 8px;
`

const PopoverDescription = styled.p`
  font-size: 14px;
  color: #718096;
  line-height: 1.6;
  margin-bottom: 20px;
  text-align: start;
  white-space: pre-line;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-start;
`

const SkipButton = styled(Button)`
  background: transparent;
  color: #718096;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f7fafc;
    border-color: #cbd5e0;
  }
`

const shimmer = keyframes`
  from {
    opacity: 0;
    left: 0%;
  }
  50% {
    opacity: 1;
  }
  to {
    opacity: 0;
    left: 100%;
  }
`

export const GetStartedButton = styled(Button)`
  background: linear-gradient(135deg, rgb(206, 171, 230) 0%, #f005c5 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in;
  box-shadow: 0 2px 8px rgba(240, 5, 197, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    display: block;
    width: 0px;
    height: 86%;
    position: absolute;
    top: 7%;
    left: 0%;
    opacity: 0;
    background: #fff;
    box-shadow: 0 0 50px 30px #fff;
    transform: skewX(-20deg);
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 3px 6px 10px 0px rgba(240, 5, 197, 0.5);
    transition: all 0.2s ease-out;
  }

  &:hover::before {
    animation: ${shimmer} 0.5s 0s linear;
  }

  &:active {
    box-shadow: 0 0 0 0 transparent;
    transition: box-shadow 0.2s ease-in;
  }
`
